// A to-do's checklist in the open card: the order it is shown in, and where an item may go (Models.swift: `shownOrder`,
// `places(for:)`, `place(for:steppingBy:)`, `Todo.moveChecklistItem`; EditorProperties.swift: `submitChecklistItem`).
// "Done items at the end" (Setting.checklistDoneLast, on unless switched off, kept per device) shows the open items
// first and the done ones after them, each in their stored order; the stored order stays, so an item ticked open again
// goes back to its place. The iPhone and the Mac follow these rules; cases/checklist.json is read by the kit's
// SharedCasesChecklistTests and test/core/checklist.test.ts.

import { newID } from "./ids";
import type { ChecklistItem } from "./records";

/** Whether the item goes with the done ones: `held`, an item just ticked, keeps its old kind for a moment. */
function goesLast(item: ChecklistItem, held: string | null | undefined): boolean {
  return item.id === held ? !item.isDone : item.isDone;
}

/**
 * The checklist in the order it is shown. With `doneLast`, the open items come first and the done ones after them,
 * each in their stored order. `held` is an item just ticked: it keeps its place for a moment, so the tick is seen
 * before it moves.
 */
export function shownOrder(items: readonly ChecklistItem[], doneLast: boolean, held?: string | null): ChecklistItem[] {
  if (!doneLast) return [...items];
  return [...items.filter((item) => !goesLast(item, held)), ...items.filter((item) => goesLast(item, held))];
}

/**
 * The places of the order shown the item can be moved to, [first, last], counted with it taken out: with `doneLast`,
 * those of the items of its kind, since the others would send it back.
 */
export function places(items: readonly ChecklistItem[], id: string, doneLast: boolean, held?: string | null): [number, number] {
  const shown = shownOrder(items, doneLast, held);
  const item = shown.find((other) => other.id === id);
  if (!doneLast || !item) return [0, Math.max(shown.length - 1, 0)];
  let openCount = 0;
  while (openCount < shown.length && !goesLast(shown[openCount]!, held)) openCount += 1;
  return goesLast(item, held) ? [openCount, Math.max(shown.length - 1, openCount)] : [0, Math.max(openCount - 1, 0)];
}

/** Puts an item at another place of the stored order; `index` counts the places with it taken out. */
export function moveItem(items: readonly ChecklistItem[], id: string, index: number): ChecklistItem[] {
  const from = items.findIndex((item) => item.id === id);
  if (from < 0) return [...items];
  const next = [...items];
  const [item] = next.splice(from, 1);
  next.splice(Math.min(Math.max(index, 0), next.length), 0, item!);
  return next;
}

/**
 * Puts an item at `index` of the order shown (a drop). With `doneLast`, the order shown becomes the stored one, and
 * the item stays among the items of its kind.
 */
export function moveItemToShown(items: readonly ChecklistItem[], id: string, index: number, doneLast: boolean): ChecklistItem[] {
  const ordered = doneLast ? shownOrder(items, true) : [...items];
  const [first, last] = places(ordered, id, doneLast);
  return moveItem(ordered, id, Math.min(Math.max(index, first), last));
}

/**
 * The place of the order shown one step up (`step` -1) or down (1) from the item, among the items of its kind (the
 * Mac's ⌥⌘↑↓); null at the edge of its kind, where it stays.
 */
export function stepPlace(items: readonly ChecklistItem[], id: string, step: number, doneLast: boolean, held?: string | null): number | null {
  const index = shownOrder(items, doneLast, held).findIndex((item) => item.id === id);
  if (index < 0) return null;
  const target = index + step;
  const [first, last] = places(items, id, doneLast, held);
  return target >= first && target <= last ? target : null;
}

/**
 * Return in an item: a new empty item after it in the stored order, whose id is given back; in an empty item, the item
 * is taken away and no id is given.
 */
export function submitItem(items: readonly ChecklistItem[], id: string): { items: ChecklistItem[]; added: string | null } {
  const index = items.findIndex((item) => item.id === id);
  if (index < 0) return { items: [...items], added: null };
  if (items[index]!.title.trim() === "") return { items: items.filter((item) => item.id !== id), added: null };
  const item: ChecklistItem = { id: newID(), title: "", isDone: false };
  const next = [...items];
  next.splice(index + 1, 0, item);
  return { items: next, added: item.id };
}
